/**
 * File validation utility for upload components
 * Validates file type and size constraints
 */

import { compressImageIfNeeded } from './fileUtils';

export interface FileValidationResult {
  isValid: boolean;
  error: string | null;
  file: File;
}

export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 // 5MB
export const MAX_FILE_SIZE_MB = 5


/**
 * Validates a file against type and size constraints, with automatic image compression for files >1MB
 * @param file - The File object to validate
 * @returns Promise containing validation result, error message if invalid, and the possibly resized file
 */
export async function validateFile(file: File): Promise<FileValidationResult> {
  console.log('🔍 [FileValidation] Starting validation for:', {
    name: file.name,
    type: file.type,
    size: file.size,
    sizeMB: (file.size / (1024 * 1024)).toFixed(2)
  });

  // Define allowed file types
  const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];

  // Define size limit (5MB in bytes)
  const maxSizeBytes = MAX_FILE_SIZE_BYTES // 5MB

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    console.log('❌ [FileValidation] Invalid file type:', file.type);
    return {
      error: `Invalid file type. Only PNG, JPEG images and PDF files are allowed. Received: ${file.type}`,
      isValid: false,
      file,
    };
  }

  console.log('✅ [FileValidation] File type valid:', file.type);
  let processedFile = file;

  // Check if it's an image (PNG/JPEG) that's >1MB and compress if needed
  const isImage = file.type === "image/png" || file.type === "image/jpeg";
  const isLargerThan1MB = file.size > (1024 * 1024); // 1MB in bytes

  console.log('🔍 [FileValidation] Image check:', { isImage, isLargerThan1MB, shouldCompress: isImage && isLargerThan1MB });

  if (isImage && isLargerThan1MB) {
    console.log('🗜️ [FileValidation] Starting image compression...');
    try {
      const startTime = performance.now();
      processedFile = await compressImageIfNeeded(file);
      const endTime = performance.now();
      const compressionTime = (endTime - startTime).toFixed(2);
      
      console.log('✅ [FileValidation] Image compression completed:', {
        originalSize: file.size,
        compressedSize: processedFile.size,
        originalSizeMB: (file.size / (1024 * 1024)).toFixed(2),
        compressedSizeMB: (processedFile.size / (1024 * 1024)).toFixed(2),
        compressionRatio: ((file.size - processedFile.size) / file.size * 100).toFixed(1) + '%',
        timeMs: compressionTime
      });
    } catch (error) {
      console.error('❌ [FileValidation] Compression failed:', error);
      return {
        error: `Failed to compress image: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isValid: false,
        file,
      };
    }
  } else {
    console.log('⏭️ [FileValidation] Skipping compression - not needed');
  }

  // Check file size after potential compression
  if (processedFile.size > maxSizeBytes) {
    const fileSizeMB = (processedFile.size / (1024 * 1024)).toFixed(2);
    console.log('❌ [FileValidation] File too large after processing:', {
      sizeMB: fileSizeMB,
      maxSizeMB: MAX_FILE_SIZE_MB
    });
    return {
      error: `File size too large. Maximum size is ${MAX_FILE_SIZE_MB}MB. Current size: ${fileSizeMB}MB`,
      isValid: false,
      file: processedFile,
    };
  }

  // File is valid
  console.log('🎉 [FileValidation] File validation successful!', {
    finalSize: processedFile.size,
    finalSizeMB: (processedFile.size / (1024 * 1024)).toFixed(2)
  });

  return {
    error: null,
    isValid: true,
    file: processedFile,
  };
}

// Export constants for reuse
export const ALLOWED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "application/pdf",
] as const;
